import { httpClient } from "@/core/api/client/httpClient";
import { getStoredToken } from "@/core/auth/session/session.storage";
import { getAppEnv } from "@/core/config/env";
import { round2 } from "@/shared/utils/formatters";

export interface PlazoSimulador {
  plazo: number;
  tasa: number;
  montoMinimo: number | null;
  montoMaximo: number | null;
  bonoAnual: number | null;
}

export interface ConfiguracionSimulador {
  codigo: string;
  productNumber: string;
  periodicidad: number;
  periodicidadNombre: string;
  plazosDisponibles: PlazoSimulador[];
}

export interface GrupoSimulador {
  id: number;
  nombre: string;
  codigo: string;
  configuraciones: ConfiguracionSimulador[];
}

export interface CuotaProyeccion {
  periodo: number;
  fechaInicio: string | null;
  fechaVencimiento: string | null;
  capitalInicio: number;
  rendimiento: number;
  capitalFin: number;
  bonoAnual?: number;
  rendimientoIncremental: number;
  rendimientoAcumuladoIncremental: number;
  bonoAcumulado: number;
  crecimientoAcumuladoSinBono: number;
  crecimientoAcumulado: number;
  valorAcumuladoSinBono: number;
  valorAcumulado: number;
}

export interface SimulacionResultado {
  productoNombre: string | null;
  montoInvertido: number;
  plazo: number;
  tasaMensual: number;
  tasaAnual: number;
  costoOperativo: number;
  rendimientoTotal: number;
  valorFinal: number;
  bonoAnual: number | null;
  proyeccion: CuotaProyeccion[];
}

function normalizePlazo(plazo: PlazoSimulador): PlazoSimulador {
  return {
    ...plazo,
    tasa: round2(plazo.tasa) ?? 0,
    montoMinimo: round2(plazo.montoMinimo),
    montoMaximo: round2(plazo.montoMaximo),
    bonoAnual: round2(plazo.bonoAnual),
  };
}

function normalizeResultado(resultado: SimulacionResultado): SimulacionResultado {
  return {
    ...resultado,
    montoInvertido: round2(resultado.montoInvertido) ?? 0,
    tasaMensual: round2(resultado.tasaMensual) ?? 0,
    tasaAnual: round2(resultado.tasaAnual) ?? 0,
    costoOperativo: round2(resultado.costoOperativo) ?? 0,
    rendimientoTotal: round2(resultado.rendimientoTotal) ?? 0,
    valorFinal: round2(resultado.valorFinal) ?? 0,
    bonoAnual: round2(resultado.bonoAnual),
    proyeccion: resultado.proyeccion.map((cuota) => ({
      ...cuota,
      capitalInicio: round2(cuota.capitalInicio) ?? 0,
      rendimiento: round2(cuota.rendimiento) ?? 0,
      capitalFin: round2(cuota.capitalFin) ?? 0,
      bonoAnual: round2(cuota.bonoAnual) ?? undefined,
      rendimientoIncremental: round2(cuota.rendimientoIncremental) ?? 0,
      rendimientoAcumuladoIncremental: round2(cuota.rendimientoAcumuladoIncremental) ?? 0,
      bonoAcumulado: round2(cuota.bonoAcumulado) ?? 0,
      crecimientoAcumuladoSinBono: round2(cuota.crecimientoAcumuladoSinBono) ?? 0,
      crecimientoAcumulado: round2(cuota.crecimientoAcumulado) ?? 0,
      valorAcumuladoSinBono: round2(cuota.valorAcumuladoSinBono) ?? 0,
      valorAcumulado: round2(cuota.valorAcumulado) ?? 0,
    })),
  };
}

interface BffSimularResponse {
  success: boolean;
  data: SimulacionResultado;
  error?: { code: string; message: string };
}

interface BffProductosResponse {
  success: boolean;
  data: { grupos: GrupoSimulador[] };
}

export interface CuotaExportDto {
  periodo: number;
  capitalInicio: number;
  rendimiento: number;
  capitalFin: number;
  bonoAnual: number;
}

export interface ExportarSimulacionRequest {
  productoNombre: string | null;
  montoInvertido: number;
  plazo: number;
  tasaAnual: number;
  bonoAnual?: number;
  rendimientoTotal: number;
  valorFinal: number;
  proyeccion: CuotaExportDto[];
}

export interface ContactarSimuladorRequest {
  productNumber: string;
  monto: number;
  plazo: number;
  periodicidad: number;
  tasaAnual: number;
  bonoAnual?: number;
  rendimientoTotal: number;
  valorFinal: number;
}

export const simuladorService = {
  async listarProductos(): Promise<GrupoSimulador[]> {
    const res = await httpClient.get<BffProductosResponse>(
      "/api/simulador/productos",
    );
    return (res.data?.grupos ?? []).map((grupo) => ({
      ...grupo,
      configuraciones: grupo.configuraciones.map((configuracion) => ({
        ...configuracion,
        plazosDisponibles: configuracion.plazosDisponibles.map(normalizePlazo),
      })),
    }));
  },

  async simular(
    productNumber: string,
    monto: number,
    plazo: number,
  ): Promise<SimulacionResultado | null> {
    const res = await httpClient.post<BffSimularResponse>(
      "/api/simulador/simular",
      {
        productNumber,
        monto,
        plazo,
      },
    );
    if (!res.success) {
      throw new Error(res.error?.message ?? "Error al calcular la simulación");
    }
    return res.data ? normalizeResultado(res.data) : null;
  },

  async exportar(
    request: ExportarSimulacionRequest,
    lang = "es",
  ): Promise<void> {
    const { apiGatewayUrl } = getAppEnv();
    const token = getStoredToken();
    const base = apiGatewayUrl.endsWith("/")
      ? apiGatewayUrl
      : `${apiGatewayUrl}/`;

    const response = await fetch(`${base}api/simulador/exportar?lang=${lang}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(
        (err as { message?: string } | null)?.message ??
          "Error al generar el PDF.",
      );
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "simulacion_synergy.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async puedeContactar(
    lang = "es",
  ): Promise<{ puedeContactar: boolean; mensaje: string }> {
    const res = await httpClient.get<{
      success: boolean;
      data: { puedeContactar: boolean; mensaje: string };
      error: null | { code: string; message: string };
    }>(`/api/simulador/puede-contactar?lang=${lang}`);
    return res.data ?? { puedeContactar: true, mensaje: "" };
  },

  async descargarPdf(
    productNumber: string,
    monto: number,
    plazo: number,
    lang = "es",
    fileName?: string,
  ): Promise<void> {
    const { apiGatewayUrl } = getAppEnv();
    const token = getStoredToken();
    const base = apiGatewayUrl.endsWith("/")
      ? apiGatewayUrl
      : `${apiGatewayUrl}/`;

    const response = await fetch(`${base}api/simulador/pdf?lang=${lang}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ productNumber, monto, plazo }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(
        (err as { message?: string } | null)?.message ??
          "Error al generar el PDF.",
      );
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName ?? "simulacion_synergy.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async contactar(
    request: ContactarSimuladorRequest,
    lang = "es",
  ): Promise<void> {
    const res = await httpClient.post<{
      success: boolean;
      message?: string;
      error?: { code: string; message: string };
    }>(`/api/simulador/contactar?lang=${lang}`, request);
    if (!res.success && res.error?.code !== "CONTACTAR_ERROR") {
      throw new Error(res.error?.message ?? "Error al enviar la solicitud.");
    }
  },
};
